// Module: slideCaptureImages
// -----------------------------------------------------------------
// Receives:  the live `.reveal` element; later, the cloned one
// Produces:  same-origin blob: copies of the slide's cross-origin images
// Owns:      the lesson's fetched image blobs; blob: URLs, from load to revoke
// Does NOT:  draw anything or decide when to capture (→ slideScreenshot)
// -----------------------------------------------------------------
//
// html2canvas reloads cross-origin images with CORS and drops those that fail.
// The slide's own non-CORS load leaves a copy without CORS headers in the
// cache, which Safari hands to that reload; past the cache it succeeds.

const CSS_URL_RE = /url\(\s*["']?([^"')]+)["']?\s*\)/g;
// Slide images can be several MB each; past this, the least recently used go.
const MAX_CACHED_IMAGE_BYTES = 32 * 1024 * 1024;

// Images fetched this lesson, so a slide captured again (bite change, exit,
// lesson end) or an image shared by slides isn't downloaded again.
const imageBlobCache = new Map<string, Blob>();
let cachedImageBytes = 0;

/** Original URL → blob: URL. */
export type CaptureImageUrls = Map<string, string>;

function isCrossOriginHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return (
      parsed.protocol.startsWith("http") &&
      parsed.origin !== window.location.origin
    );
  } catch {
    return false;
  }
}

/** Cross-origin image URLs inside the slide: <img> sources and CSS backgrounds. */
export function findSlideImageUrls(revealEl: Element): string[] {
  const urls = new Set<string>();
  const elements = [revealEl, ...Array.from(revealEl.querySelectorAll("*"))];
  elements.forEach((el) => {
    if (el instanceof HTMLImageElement) urls.add(el.currentSrc || el.src);
    const backgroundImage = getComputedStyle(el).backgroundImage;
    CSS_URL_RE.lastIndex = 0;
    for (
      let match = CSS_URL_RE.exec(backgroundImage);
      match;
      match = CSS_URL_RE.exec(backgroundImage)
    ) {
      urls.add(match[1]);
    }
  });
  return Array.from(urls).filter(isCrossOriginHttpUrl);
}

function cacheImageBlob(url: string, blob: Blob) {
  // One image past the limit would evict everything and still not fit.
  if (blob.size > MAX_CACHED_IMAGE_BYTES) return;
  cachedImageBytes -= imageBlobCache.get(url)?.size ?? 0;
  imageBlobCache.set(url, blob);
  cachedImageBytes += blob.size;
  // A Map iterates in insertion order, and a hit re-inserts: oldest first.
  for (const [oldUrl, oldBlob] of imageBlobCache) {
    if (cachedImageBytes <= MAX_CACHED_IMAGE_BYTES) break;
    imageBlobCache.delete(oldUrl);
    cachedImageBytes -= oldBlob.size;
  }
}

/** Frees the cached images; called when the lesson unmounts. */
export function clearCaptureImageCache(): void {
  imageBlobCache.clear();
  cachedImageBytes = 0;
}

// A failed or aborted fetch is not cached, so the next capture tries again.
async function fetchImageBlob(
  url: string,
  signal: AbortSignal,
): Promise<Blob | null> {
  const cached = imageBlobCache.get(url);
  if (cached) {
    imageBlobCache.delete(url);
    imageBlobCache.set(url, cached);
    return cached;
  }
  try {
    const response = await fetch(url, {
      mode: "cors",
      cache: "no-store",
      signal,
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    cacheImageBlob(url, blob);
    return blob;
  } catch {
    return null;
  }
}

/**
 * Fetches the slide's cross-origin images past the browser cache, once per
 * lesson.
 *
 * Input:   the live `.reveal` element, the longest wait in ms
 * Output:  blob: URLs for the images that arrived in time; the rest are left
 *          out, and the capture goes ahead without them as it always did
 * Ensures: never rejects; whatever is still loading is aborted
 */
export async function loadSlideImagesPastCache(
  revealEl: Element,
  timeoutMs: number,
): Promise<CaptureImageUrls> {
  const urls = findSlideImageUrls(revealEl);
  const blobUrls: CaptureImageUrls = new Map();
  if (urls.length === 0) return blobUrls;
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);
  await Promise.all(
    urls.map(async (url) => {
      const blob = await fetchImageBlob(url, controller.signal);
      if (blob) blobUrls.set(url, URL.createObjectURL(blob));
    }),
  );
  clearTimeout(timerId);
  return blobUrls;
}

/**
 * Each `url(...)` in `text` whose URL has a blob: copy, pointed at it. Whole
 * matches only, so a URL that is a prefix of another (`a.png`, `a.png?v=2`)
 * leaves the longer one alone.
 */
function replaceImageUrls(text: string, blobUrls: CaptureImageUrls): string {
  return text.replace(CSS_URL_RE, (match, url: string) => {
    const blobUrl = blobUrls.get(url);
    return blobUrl ? `url("${blobUrl}")` : match;
  });
}

/**
 * Points the clone's images at their blob: copies.
 *
 * Mutates: `src` and inline `background-image` inside the cloned `.reveal`
 */
export function applyBlobImagesToClone(
  clonedReveal: Element,
  blobUrls: CaptureImageUrls,
): void {
  if (blobUrls.size === 0) return;
  const elements = [
    clonedReveal,
    ...Array.from(clonedReveal.querySelectorAll("*")),
  ];
  elements.forEach((el) => {
    // The clone lives in html2canvas's iframe, so instanceof would miss.
    if (el.tagName === "IMG") {
      const img = el as HTMLImageElement;
      const blobUrl = blobUrls.get(img.currentSrc || img.src);
      if (blobUrl) {
        img.srcset = "";
        img.src = blobUrl;
      }
    }
    const style = (el as HTMLElement).style;
    if (!style?.backgroundImage) return;
    style.backgroundImage = replaceImageUrls(style.backgroundImage, blobUrls);
  });
}

/**
 * The capture CSS with the slide's image URLs pointed at their blob: copies,
 * for backgrounds set by a class rather than inline.
 */
export function applyBlobImagesToCss(
  css: string,
  blobUrls: CaptureImageUrls,
): string {
  return replaceImageUrls(css, blobUrls);
}

export function revokeCaptureImageUrls(blobUrls: CaptureImageUrls): void {
  blobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
}
